import memoizeOne from 'memoize-one';
import { createSelector } from 'reselect';

import { findPriorToOption, getPriorToDisplayName } from 'utils/priorTo';

import { selectEnabledTaskTypes, selectTaskTypes } from 'store/system/selectors';

import { selectConditionSetById } from './conditionSets.selectors';
import { selectPriorToAllOptions } from './priorTo.selectors';
import { selectSlaGroups } from './slaGroups.selectors';
import { selectUnderwriting } from './underwriting.selectors';

const selectConditions = createSelector(selectUnderwriting, ({ conditions }) => conditions);

export const selectConditionsList = createSelector(selectConditions, Object.values);

export const selectConditionById = memoizeOne(id =>
  createSelector(selectConditions, conditions => conditions?.[id]),
);

export const selectAvailableConditionsByConditionSetId = memoizeOne(id =>
  createSelector([selectConditionsList, selectConditionSetById(id)], (conditions, conditionSet) =>
    conditions.filter(
      condition => !conditionSet.links.some(({ conditionId }) => conditionId === condition.id),
    ),
  ),
);

const selectEnabledConditionsList = createSelector(selectConditionsList, conditions =>
  conditions.filter(({ isEnabled }) => isEnabled),
);

export const selectConditionsWithTaskTypes = createSelector(
  [selectConditionsList, selectEnabledTaskTypes],
  (conditionsList, taskTypes) =>
    conditionsList.reduce((acc, condition) => {
      acc[condition.id] = {
        ...condition,
        taskTypes: condition.taskTypeIds.reduce((acc, taskTypeId) => {
          if (taskTypes[taskTypeId]) {
            acc.push(taskTypes[taskTypeId]);
          }

          return acc;
        }, []),
      };

      return acc;
    }, {}),
);

export const selectConditionsWithSlaGroupName = createSelector(
  [selectConditionsList, selectSlaGroups],
  (conditions, slaGroups) =>
    conditions.map(condition => ({
      ...condition,
      slaGroupName: slaGroups[condition.slaGroupId]?.name,
    })),
);

export const selectConditionsWithNumberName = createSelector(
  [selectEnabledConditionsList, selectPriorToAllOptions],
  (conditions, priorToOptions) =>
    conditions.map(condition => ({
      ...condition,
      name: `${condition.number} - ${condition.message}`,
      priorToTypeId:
        findPriorToOption({
          options: priorToOptions,
          priorToTypeId: condition.priorToTypeId,
          priorToType: condition.priorToType,
        })?.value ??
        condition.priorToTypeId ??
        null,
    })),
);

export const selectConditionsCsvData = createSelector(
  [selectConditionsWithSlaGroupName, selectTaskTypes],
  (conditions, taskTypes) =>
    conditions.map(
      ({
        number,
        message,
        borrowerMessage,
        priorToType,
        priorToTypeName,
        slaGroupName,
        taskTypeIds,
        isEnabled,
        programLoanTypes,
      }) => ({
        number,
        message,
        borrowerMessage,
        priorToType: getPriorToDisplayName({ priorToTypeName, priorToType }),
        slaGroupName,
        taskTypes: taskTypeIds.map(taskTypeId => taskTypes[taskTypeId]?.name)?.join(', '),
        programLoanTypes: programLoanTypes?.join(', '),
        isEnabled: isEnabled ? 'Yes' : 'No',
      }),
    ),
);
